import { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Typography,
    Box,
    MenuItem,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
} from "@mui/material";
import { Visibility, VisibilityOff, ContentCopy } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Company } from "../../../types/companyTypes";
import SuperAdminCompanyService from './../../../service/superadmin/companyService';
import { useNavigate } from "react-router-dom";
import SuperAdminUserService from "../../../service/superadmin/userService";


const AddUser = ({ onClose }: { onClose: () => void }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: "",
        username: "",
        email: "",
        address: "",
        phone: "",
        country_code: "+65",
        role: "user",
        status: "1",
        password: "",
        confirmPassword: "",
        company_id: "",
        is_company_admin: "2",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<Company[]>([]);


    const fetchCompanies = async () => {
        try {
            const companies = await SuperAdminCompanyService.getCompanyList();
            setCompanyList(companies);
            if (companies.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    company_id: companies[0].id.toString(),
                }));
            }
        } catch (error) {
            toast.error("Failed to load company list.");
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const copyPassword = () => {
        if (!formData.password) {
            toast.error("No password to copy!");
            return;
        }
        navigator.clipboard.writeText(formData.password);
        toast.info("Password copied to clipboard!");
    };

    // Function to generate a random password
    const generatePassword = () => {
        const newPassword = Math.random().toString(36).slice(-10); // Generate a random 10-character password
        setFormData((prev) => ({ ...prev, password: newPassword, confirmPassword: newPassword }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        let updatedForm = {
            ...formData,
            [name]: name === "company_id" ? value.toString() : value,
        };

        // If role is changed to company_admin, force is_company_admin to "2"`
        if (name === "role" && value === "companyadmin") {
            updatedForm.is_company_admin = "1";
            // updatedForm.role = "companyadmin";
        }

        setFormData(updatedForm);
        setError("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            const { confirmPassword, ...dataToSend } = formData;

            if (formData.role === "companyadmin") {
                dataToSend.role = "user";
                dataToSend.is_company_admin = "1";
            }

            await SuperAdminUserService.addUser(dataToSend);

            toast.success("User added successfully!");
            onClose();

            window.location.reload();

        } catch (error) {
            console.error("Failed to add user", error);
            toast.error("Failed to add user.");
        } finally {
            setLoading(false);
        }
    };

    const isSuperAdmin = formData.role === "superadmin" || formData.role === 'admin';

    return (
        <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ width: "600px", p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Add New User
            </Typography>
            <Grid container spacing={2}>
                {/* First Row - Name & Username */}
                <Grid item xs={6}>
                    <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} required />
                </Grid>
                <Grid item xs={6}>
                    <TextField fullWidth label="Username" name="username" value={formData.username} onChange={handleChange} autoComplete="off" required />
                </Grid>

                {/* Second Row - Email (Full Width) */}
                <Grid item xs={8}>
                    <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                </Grid>
                <Grid item xs={4}>
                    <TextField select fullWidth label="Role" name="role" value={formData.role} onChange={handleChange}>
                        <MenuItem value="user" disabled={companyList.length === 0}>User</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="superadmin">Super Admin</MenuItem>
                        <MenuItem value="companyadmin">Company Admin</MenuItem>
                    </TextField>
                </Grid>

                {/* Third Row - Country Code & Phone */}
                <Grid item xs={4}>
                    <TextField
                        select
                        fullWidth
                        label="Country Code"
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleChange}
                    >
                        <MenuItem value="+1">+1 (US)</MenuItem>
                        <MenuItem value="+44">+44 (UK)</MenuItem>
                        <MenuItem value="+33">+33 (France)</MenuItem>
                        <MenuItem value="+49">+49 (Germany)</MenuItem>
                        <MenuItem value="+65">+65 (Singapore)</MenuItem>
                        <MenuItem value="+60">+60 (Malaysia)</MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={8}>
                    <TextField fullWidth label="Phone" name="phone" type="number" value={formData.phone} onChange={handleChange} required />
                </Grid>

                {/* Fourth Row - Address (Full Width) */}
                <Grid item xs={12}>
                    <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} required />
                </Grid>

                {/* Fifth Row - Password & Confirm Password */}
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="off"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={copyPassword} edge="end" disabled={loading}>
                                        <ContentCopy />
                                    </IconButton>
                                    <IconButton onClick={() => setShowPassword(!showPassword)} disabled={loading}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Company Dropdown */}
                <Grid item xs={12}>
                    <TextField
                        select
                        fullWidth
                        label="Company"
                        name="company_id"
                        value={formData.company_id || ""} // Ensure it's not undefined
                        onChange={handleChange}
                        disabled={companyList.length === 0 || isSuperAdmin}
                        required
                    >
                        {companyList.map((company) => (
                            <MenuItem key={company.id} value={company.id.toString()}> {/* Convert ID to string */}
                                {company.company_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Buttons */}
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Stack direction="row" spacing={2}>
                            <Button variant="contained" color="primary" type="submit" disabled={loading}>
                                {loading ? "Saving..." : "Save User"}
                            </Button>
                            <Button variant="contained" color="secondary" onClick={generatePassword} disabled={loading}>
                                Generate Password
                            </Button>
                        </Stack>
                        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddUser;
